import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  FileText, Search, AlertTriangle, CheckCircle2, ChevronRight, Filter,
  Calendar, MapPin, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import WorkerAvatar from '../../components/shared/WorkerAvatar';
import { formatApiError } from '../../lib/apiError';

/**
 * Shift Records — permanent, searchable audit trail of every completed
 * shift (not payroll). Every completed shift IS a record; created
 * automatically. Managers can filter by reviewed status, discrepancy
 * presence, and free-text search across worker name / branch / notes.
 * Clicking a row opens the read-only shift log detail (routes at
 * /manager/shift-records/:shiftId → LogsPage in read-only mode).
 */
export default function ShiftRecordsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [reviewStatus, setReviewStatus] = useState('all'); // all | not_reviewed | under_review | reviewed | resolved | archived
  const [hasDisc, setHasDisc] = useState('all');           // all | yes | no
  const debounceRef = useRef(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (reviewStatus !== 'all') params.set('reviewStatus', reviewStatus);
      if (hasDisc !== 'all') params.set('hasDiscrepancy', hasDisc);
      params.set('limit', '300');
      const r = await api.get(`/manager/shift-records?${params.toString()}`);
      setRecords(r.data?.records || []);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load shift records'));
    } finally {
      setLoading(false);
    }
  }, [api, q, reviewStatus, hasDisc]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchRecords, 250);
    return () => clearTimeout(debounceRef.current);
  }, [fetchRecords]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return iso; }
  };

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto" data-testid="shift-records-page">
      <nav className="text-[12px] text-slate-500 flex items-center gap-1">
        <button onClick={() => navigate('/manager')} className="hover:text-slate-800">Dashboard</button>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-slate-800 font-semibold">Shift Records</span>
      </nav>

      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Public_Sans']">Shift Records</h1>
        <p className="text-slate-500 text-[13px] mt-0.5">
          Permanent audit trail of every completed shift. Search by worker, branch, date, or discrepancy status.
        </p>
      </div>

      {/* Filter bar */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-2 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search worker, branch, date, shift ID or notes…"
              className="h-11 pl-10 rounded-lg border-slate-200 text-[13px]"
              data-testid="shift-records-search"
            />
          </div>
          <div>
            <select
              value={reviewStatus}
              onChange={(e) => setReviewStatus(e.target.value)}
              className="w-full h-11 rounded-lg border border-slate-200 text-[13px] px-2 bg-white"
              data-testid="shift-records-filter-review-status"
            >
              <option value="all">All statuses</option>
              <option value="not_reviewed">Not Reviewed</option>
              <option value="under_review">Under Review</option>
              <option value="reviewed">Reviewed</option>
              <option value="resolved">Resolved</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div>
            <select
              value={hasDisc}
              onChange={(e) => setHasDisc(e.target.value)}
              className="w-full h-11 rounded-lg border border-slate-200 text-[13px] px-2 bg-white"
              data-testid="shift-records-filter-discrepancy"
            >
              <option value="all">Any discrepancy</option>
              <option value="yes">Has discrepancy</option>
              <option value="no">No discrepancy</option>
            </select>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between text-[11.5px] text-slate-500">
          <span data-testid="shift-records-count">{records.length} record{records.length === 1 ? '' : 's'}</span>
          {(q || reviewStatus !== 'all' || hasDisc !== 'all') && (
            <button
              onClick={() => { setQ(''); setReviewStatus('all'); setHasDisc('all'); }}
              className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-800"
              data-testid="shift-records-clear-filters"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center gap-2 p-6 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading records…
        </div>
      ) : records.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center" data-testid="shift-records-empty">
          <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-600">No shift records match your filters</p>
          <p className="text-[12px] text-slate-500 mt-1">Completed shifts appear here automatically.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="shift-records-list">
          {records.map((r) => (
            <li
              key={r.id}
              onClick={() => navigate(`/manager/shift-records/${r.id}`, { state: { from: 'shift-records' } })}
              className="rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-300 cursor-pointer transition-colors"
              data-testid={`shift-record-row-${r.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <WorkerAvatar
                    profilePhoto={r.assignedWorkerPhoto}
                    name={r.assignedWorkerName}
                    size={40}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-[13.5px] font-semibold text-slate-900">{r.assignedWorkerName || 'Unassigned'}</p>
                      {r.hasDiscrepancy && (
                        <Badge className="bg-rose-100 text-rose-700 border border-rose-200 text-[10px] font-bold uppercase tracking-wider" data-testid={`shift-record-has-discrepancy-${r.id}`}>
                          <AlertTriangle className="h-3 w-3 mr-0.5" /> Discrepancy
                        </Badge>
                      )}
                      {(() => {
                        const rs = r.reviewStatus || (r.shiftReviewed ? 'reviewed' : 'not_reviewed');
                        const map = {
                          not_reviewed: { label: 'Not Reviewed', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
                          under_review: { label: 'Under Review', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
                          reviewed:     { label: 'Reviewed',     cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
                          resolved:     { label: 'Resolved',     cls: 'bg-blue-100 text-blue-800 border-blue-200' },
                          archived:     { label: 'Archived',     cls: 'bg-slate-200 text-slate-600 border-slate-300' },
                        };
                        const b = map[rs] || map.not_reviewed;
                        return (
                          <Badge className={`${b.cls} border text-[10px] font-bold uppercase tracking-wider`} data-testid={`shift-record-review-status-${r.id}`}>
                            {b.label}
                          </Badge>
                        );
                      })()}
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider capitalize">
                        {r.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap text-[12px] text-slate-500">
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {r.branchName}</span>
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(r.date)}</span>
                      <span>{r.scheduledStartTime} – {r.scheduledEndTime}</span>
                    </div>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 shrink-0" />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
